import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Form, Image, Media, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import { faPen, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Select from 'react-select';
import ToggleSwitch from 'react-switch';

import { anchovy, grape } from 'lib/css/colors';
import { areYouSure, SwalToast } from 'lib/utils/toast';
import BrandContentQAIcon from 'components/manage_brands/BrandContentQAIcon';
import { BrandVerificationStatus } from 'lib/enums/BrandVerificationStatus';
import EditableProperty from 'components/manage_brands/brand_details/EditableProperty';
import { faSquareCheck } from 'images/icons/fsa-monotone-svg-icons';
import { BRANDS_DELETE_LOGO, GLOBAL_VENDORS_FOR_DROPDOWN } from 'lib/networking/endpoints';
import placeholderProductImage from 'images/placeholder-img-product-v2.svg';
import pointerOnHover from 'lib/css/pointerOnHover';
import UserPermission from 'lib/enums/UserPermission';
import useUserPermissions from 'lib/hooks/useUserPermissions';
import useGet from 'lib/hooks/useGet';
import useDelete from 'lib/hooks/useDelete';

function BrandLogoSplitView({ brand, updateBrand, setShowBrandLogoModal, refetch }) {
  const hasUserPermissions = useUserPermissions();
  const [isEditing, setIsEditing] = useState(false);
  const [updatedBrand, setUpdatedBrand] = useState({ id: brand.id });
  const [selectedVendor, setSelectedVendor] = useState({
    value: brand.private_label_distributor_id,
    label: brand.private_label_distributor?.name,
  });

  const canEditLockedAttributes = useMemo(
    () => hasUserPermissions([UserPermission.ADMIN_OPERATIONS]),
    [hasUserPermissions],
  );

  const { data: { data: globalVendors } = {} } = useGet(GLOBAL_VENDORS_FOR_DROPDOWN);

  const { deleteData: deleteLogo, loading: deleteLogoLoading } = useDelete(
    BRANDS_DELETE_LOGO,
    false,
    () => {
      SwalToast('success', 'Brand logo deleted successfully');
      refetch && refetch();
    },
    () => SwalToast('error', 'Failed to delete brand logo'),
  );

  const handleDeleteLogo = () => {
    areYouSure(
      () => deleteLogo({ brand_id: brand.id }),
      'Are you sure you want to delete this brand logo?',
    );
  };

  return (
    <div className={classNames(css(styles.container), 'mt-5')}>
      <Media
        className={
          !brand.locked_attributes.includes('logo_url') || canEditLockedAttributes
            ? pointerOnHover
            : null
        }
        onClick={
          !brand.locked_attributes.includes('logo_url') || canEditLockedAttributes
            ? () => {
                setShowBrandLogoModal(true);
              }
            : () => {}
        }
      >
        <Image
          className={css(styles.square)}
          src={brand?.logo_url || placeholderProductImage}
          alt="Brand Logo"
        />
        {brand.locked_attributes.includes('logo_url') && (
          <div>
            <BrandContentQAIcon brand={brand} attribute="logo_url" />
          </div>
        )}
      </Media>
      <div className="d-flex align-items-center mb-2">
        {(!brand.locked_attributes.includes('logo_url') || canEditLockedAttributes) && (
          <p className="small text-muted mb-0 mr-3">* Click on image to change logo</p>
        )}
        {brand?.logo_url &&
          (!brand.locked_attributes.includes('logo_url') || canEditLockedAttributes) && (
            <OverlayTrigger overlay={<Tooltip>Delete brand logo</Tooltip>}>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={e => {
                  e.stopPropagation();
                  handleDeleteLogo();
                }}
                disabled={deleteLogoLoading}
              >
                {deleteLogoLoading ? (
                  <Spinner animation="border" role="status" size="sm" />
                ) : (
                  <>
                    <FontAwesomeIcon icon={faTrashAlt} className="mr-1" />
                    Delete Logo
                  </>
                )}
              </Button>
            </OverlayTrigger>
          )}
      </div>
      <h3 className="font-weight-bold py-4 ">{brand.name}</h3>
      <div>
        <p className={css(styles.label)}>ID</p>
        <p className={css(styles.value)}>{brand.id}</p>
      </div>
      <div>
        <p className={css(styles.label)}>Cut and Dry ID</p>
        {isEditing ? (
          <Form onBlur={() => setIsEditing(false)} className={css(styles.row)}>
            <FontAwesomeIcon
              className={classNames(pointerOnHover, css(styles.editIcon))}
              icon={faSquareCheck}
              onMouseDown={() => {
                updateBrand(updatedBrand);
                setIsEditing(false);
              }}
              size="lg"
            />
            <EditableProperty
              label={'cnd_id'}
              brand={brand}
              setIsEditing={setIsEditing}
              setUpdatedBrand={setUpdatedBrand}
            />
          </Form>
        ) : (
          <div className={css(styles.row)}>
            <FontAwesomeIcon
              className={classNames(pointerOnHover, css(styles.editIcon))}
              icon={faPen}
              onClick={() => {
                setIsEditing(true);
              }}
            />
            <p className={css(styles.value)}>{brand.cnd_id}</p>
          </div>
        )}
      </div>
      <div className={css(styles.toggleSwitchContainer)}>
        <p className={css(styles.label)}>Verification Status</p>
        <ToggleSwitch
          onColor={grape}
          className={css(styles.value)}
          checked={brand.verification_status === BrandVerificationStatus.VERIFIED}
          checkedIcon={false}
          uncheckedIcon={false}
          onChange={() =>
            updateBrand({
              id: brand.id,
              verification_status:
                brand.verification_status === BrandVerificationStatus.VERIFIED
                  ? BrandVerificationStatus.UNVERIFIED
                  : BrandVerificationStatus.VERIFIED,
            })
          }
        />
      </div>
      {brand.verification_status === BrandVerificationStatus.VERIFIED && (
        <div className={css(styles.toggleSwitchContainer)}>
          <p className={css(styles.label)}>Verified by</p>
          <p className={css(styles.value)}>{brand.verified_by?.name || '-'}</p>
        </div>
      )}
      <div>
        <p className={css(styles.label)}>Linked Products</p>
        <p className={css(styles.value)}>{brand.product_count}</p>
      </div>
      <div className={css(styles.toggleSwitchContainer)}>
        <p className={css(styles.label)}>Retail Brand</p>
        <ToggleSwitch
          onColor={grape}
          className={css(styles.value)}
          checked={brand.is_retail}
          checkedIcon={false}
          uncheckedIcon={false}
          onChange={() =>
            updateBrand({
              id: brand.id,
              is_retail: !brand.is_retail,
            })
          }
        />
      </div>
      <div className={css(styles.toggleSwitchContainer)}>
        <p className={css(styles.label)}>Is Discontinued</p>
        <ToggleSwitch
          onColor={grape}
          className={css(styles.value)}
          checked={brand.is_discontinued}
          checkedIcon={false}
          uncheckedIcon={false}
          onChange={() =>
            updateBrand({
              id: brand.id,
              is_discontinued: !brand.is_discontinued,
            })
          }
        />
      </div>
      <div className="pr-5">
        <p className={css(styles.label)}>Select Private Label Distributor</p>
        <Select
          value={selectedVendor}
          options={globalVendors?.map(vendor => ({
            value: vendor.id,
            label: vendor.name,
          }))}
          onChange={e =>
            areYouSure(
              () =>
                updateBrand({
                  id: brand.id,
                  private_label_distributor_id: e?.value || null,
                }),
              `Are you sure you want to update the private label distributor?`,
            )
          }
          placeholder="Search vendor..."
          isSearchable
          isClearable
        />
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingLeft: '1.9rem',
  },
  label: {
    display: 'inline',
    fontWeight: 'bold',
    color: anchovy,
  },
  editIcon: {
    marginRight: '0.75rem',
  },
  row: {
    display: 'flex',
    marginLeft: '-1.65rem',
  },
  square: {
    width: '200px',
    height: '200px',
    objectFit: 'contain',
    border: '2px solid ' + anchovy,
    marginBottom: '1rem',
    marginRight: '0.2rem',
    padding: '0.5rem',
  },
  value: {
    display: 'block',
  },
  toggleSwitchContainer: {
    marginBottom: '1rem',
  },
});

BrandLogoSplitView.propTypes = {
  brand: PropTypes.object,
  updateBrand: PropTypes.func,
  setShowBrandLogoModal: PropTypes.func,
  refetch: PropTypes.func,
};

export default BrandLogoSplitView;
